import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetTripsQuery, usePostTripsMutation } from '../../store/backendApi';

/**
 * Platform-agnostic hook for creating a new trip with modal UI.
 * Manages modal state, form validation, and trip creation.
 */
export function useCreateTripModal() {
  const navigate = useNavigate();
  const [postTrip, { data: postTripResponse, isLoading: postTripLoading }] = usePostTripsMutation();
  const { refetch: refetchTrips } = useGetTripsQuery('admin');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  // Loading state
  const [waitingForTrip, setWaitingForTrip] = useState(false);

  // Validation
  const isTitleValid = title.trim().length > 0;
  const areDatesValid = startDate !== null && endDate !== null;
  const isFormValid = isTitleValid && areDatesValid;

  const refetchTripsUntilTripExists = useCallback(
    (tripId: string) => {
      setWaitingForTrip(true);
      refetchTrips().then((response) => {
        if (response.data) {
          const foundTrip = response.data.some((trip) => trip.id === tripId);
          if (foundTrip) {
            // Trip found in list, redirect to trip page
            setWaitingForTrip(false);
            navigate(`/trips/${tripId}`);
            return;
          }
        }
        setTimeout(() => {
          refetchTripsUntilTripExists(tripId);
        }, 500);
      });
    },
    [refetchTrips, navigate],
  );

  useEffect(() => {
    if (postTripResponse) {
      const tripId = postTripResponse.tripId;
      refetchTripsUntilTripExists(tripId);
    }
  }, [postTripResponse, refetchTripsUntilTripExists]);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    // Reset form when closing
    setTitle('');
    setStartDate(null);
    setEndDate(null);
  }, []);

  const handleDateChange = useCallback((newStartDate: string | null, newEndDate: string | null) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, []);

  const createTrip = useCallback(async () => {
    // Validate before creating
    if (!isFormValid) {
      return;
    }

    await postTrip({
      title: title.trim(),
      startDate: startDate!,
      endDate: endDate!,
      description: '',
      headerFileName: '',
      shareMode: 'PUBLIC',
      sortOrder: 'DESC',
    });

    // Close modal after successful creation
    closeModal();
  }, [postTrip, title, startDate, endDate, isFormValid, closeModal]);

  const isLoading = postTripLoading || waitingForTrip;

  return {
    // Modal state
    isModalOpen,
    openModal,
    closeModal,

    // Form state
    title,
    setTitle,
    startDate,
    endDate,
    handleDateChange,

    // Validation
    isFormValid,
    isTitleValid,
    areDatesValid,

    // Actions
    createTrip,
    isLoading,
  };
}
